import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import { getRequest, putRequest } from '../../helpers/apiHandler';
import config from '../../config/Constant';
import CreditEditBalanceModel from './CreditEditBalanceModel';
import { TransferIcon } from '../../layouts/SVG/ManagerIcons';
import PerkesoTransferCreditModel from './PerkesoTransferCreditModel';
import ListPage from '../ListPage';

const white_label_perkeso = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

const Credit = () => {
  const { access_token } = useSelector((state) => state.auth.user);
  const [loadingData, setLoadingData] = useState(false);
  const [search, setSearch] = useState('');
  const [credit_data, setCreditData] = useState([]);
  const [memberList, setMemberList] = useState([]);
  const [columns, setColumns] = useState([]);
  const [searchData, setSearchData] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [modelMode, setModelMode] = useState('balance');
  const [showEditBalanceModal, setShowEditBalanceModal] = useState(false);
  const [showPerkesoEditBalance, setPerkesoEditBalance] = useState(false);
  const [selectedCreditData, setSelectedCreditData] = useState({});
  const [showTransferModal , setShowTransferModal] = useState(false);

  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const getCreditData = () => {
    setLoadingData(true);
    const params = { headers: { Authorization: access_token.token } };
    getRequest(`/admin/credit-management`, params, (res) => {
      if (res.success) {
        const credit_data = res.data.credit_data;
        const member_list = res.data.member_list;
        setCreditData(credit_data);
        setMemberList(member_list);
        setSearchData(credit_data);
        generateColumns(credit_data);
      } else {
        setAlertState({
          message: 'There was an issue while fetching user credits',
          show: true,
          variant: 'danger',
        });
      }
      setLoadingData(false);
    });
  };

  const handleCurrencyChange = (user_id, iso3) => {
    putRequest(`/admin/credit-currency-update`, { user_id, iso3 }, access_token.token, (res) => {
      if (res.success) {
        getCreditData();
        setAlertState({
          message: 'Currency updated successfully.',
          show: true,
          variant: 'success',
        });
      } else {
        setAlertState({
          message: 'Failed to update currency',
          show: true,
          variant: 'danger',
        });
      }
    });
  };

  const generateColumns = (credit_data) => {
    if (credit_data.length > 0) {
      const currencies = ['USD', 'EUR', 'MYR'];
      const base_columns = [
        { name: 'User ID', sortable: true, selector: (row) => row.user_id },
        { name: 'Name', sortable: true, selector: (row) => row.user_name },
        { name: 'Email', sortable: true, selector: (row) => row.email },
        { name: 'Role', sortable: true, selector: (row) => row.role },
        {
          name: 'Credit Balance',
          sortable: true,
          selector: (row) => row.available_balance,
          cell: (row) => (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span>
                {!white_label_perkeso
                  ? `${row.symbol} ${row.available_balance}`
                  : row.available_balance}
              </span>
              <i
                style={{ marginLeft: '20px', cursor: 'pointer', color: '#33B8FF' }}
                className="fas fa-edit"
                onClick={() => {
                  setSelectedCreditData(row);
                  if (white_label_perkeso) {
                    setPerkesoEditBalance(true);
                  } else {
                    setShowEditBalanceModal(true);
                  }
                  setModelMode('balance');
                }}
              />
            </div>
          ),
        },
      ];
      const elfo_columns = [
        {
          name: 'Currency',
          sortable: true,
          cell: (row) => (
            <select
              value={row.iso3}
              onChange={(e) => handleCurrencyChange(row.user_id, e.target.value)}
              style={{ border: 'none', background: 'transparent' }}
            >
              {currencies.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          ),
        },
      ];
      const perkeso_columns = [
        {
          name: 'Credit Balance Alert',
          sortable: true,
          selector: (row) => row.alert_balance,
          cell: (row) => (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span>{row.alert_balance}</span>
              <i
                className="fas fa-edit"
                onClick={() => {
                  setSelectedCreditData(row);
                  setShowEditBalanceModal(true);
                  setModelMode('balance_alert');
                }}
                style={{ marginLeft: '20px', cursor: 'pointer', color: '#33B8FF' }}
              ></i>
            </div>
          ),
        },
      ];

      if (white_label_perkeso) {
        base_columns.push(...perkeso_columns);
      }
      if (!white_label_perkeso) {
        base_columns.push(...elfo_columns);
      }
      setColumns(base_columns);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setShowEditBalanceModal(false);
    setPerkesoEditBalance(false);
    setShowTransferModal(false);
  };

  const handleCreditUpdate = (id, amount, update_operation) => {
    var response_message;
    if (update_operation === 'topup') response_message = 'Credit added successfully';
    if (update_operation === 'deduct') response_message = 'Credit deducted successfully';
    if (update_operation === 'alert_balance')
      response_message = 'Credit balance alert updated successfully';

    const params = {
      update_operation: update_operation,
      id: id,
      amount: amount,
    };
    putRequest(`/admin/credit-update`, params, access_token.token, (res) => {
      if (res.success) {
        getCreditData();
        closeModal();
        setAlertState({
          message: response_message,
          show: true,
          variant: 'success',
        });
      } else {
        setAlertState({
          message: res.message,
          show: true,
          variant: 'danger',
        });
      }
    });
  };

  const onSearch = (evt) => {
    const { value } = evt.target;
    const filteredData = credit_data.filter(
      (item) =>
        item.user_name.toLowerCase().includes(value.toLowerCase()) ||
        item.email.toLowerCase().includes(value.toLowerCase())
    );

    setSearch(value);
    setSearchData(value ? filteredData : credit_data);
  };

  useEffect(() => {
    getCreditData();
  }, []);

  useEffect(() => {
    if (alertState.show) {
      setTimeout(() => {
        setAlertState({
          message: '',
          show: false,
          variant: '',
        });
      }, config.notify_time);
    }
  }, [alertState]);

  return (
    <ListPage
      title="Credit Management"
      search={search}
      onSearch={onSearch}
      alert={alertState}
      setAlert={setAlertState}
      columns={columns}
      data={searchData}
      loading={loadingData}
      actions={
        <>
          {white_label_perkeso && (
          <Button variant="primary" onClick={() => setShowTransferModal(true)} style={{ minWidth: 200 }}>
            <TransferIcon />
            <span style={{ marginLeft: '8px' }}>Transfer Credit</span>
          </Button>
          )}
        </>
      }
    >
      <CreditEditBalanceModel
        show={showEditBalanceModal}
        onClose={closeModal}
        handleCreditUpdate={handleCreditUpdate}
        creditData={selectedCreditData}
        modelMode={modelMode}
      />
      <PerkesoTransferCreditModel 
        show={showPerkesoEditBalance}
        onClose={closeModal}
        getCreditData={getCreditData}
        creditData={selectedCreditData}
        memberList={memberList}
      />
      <PerkesoTransferCreditModel 
        show={showTransferModal}
        onClose={closeModal}
        getCreditData={getCreditData}
        creditData={selectedCreditData}
        memberList={memberList}
        showTransferModal={showTransferModal}
      />
    </ListPage>
  );
};

export default Credit;
